import { useRef, useState } from "react";
import { Upload, Image as ImageIcon, FileText, Loader2, X, ExternalLink, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MediaUploadField
 * Drag-drop + click upload field for SCFR publication editor.
 * - kind: "image" | "pdf"
 * - value: { id, public_url, title } | string (legacy URL) | null
 * - onChange(newValue): receives the full media doc (for new uploads)
 *                       or a string (when manually pasted via "Use external URL")
 */
export default function MediaUploadField({
  kind = "image",
  label,
  value,
  onChange,
  helpText,
  testIdPrefix = "media",
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [manualUrl, setManualUrl] = useState("");
  const [showManual, setShowManual] = useState(false);

  const accept = kind === "image"
    ? "image/jpeg,image/png,image/webp,image/gif"
    : "application/pdf";
  const maxMb = kind === "image" ? 10 : 25;

  // Normalize value -> display URL + title
  const displayUrl =
    typeof value === "string"
      ? value
      : value?.public_url
        ? `${API_URL}${value.public_url}`
        : null;
  const displayTitle =
    typeof value === "object" && value?.title
      ? value.title
      : typeof value === "string" && value
        ? value.split("/").pop()
        : null;

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);

    if (file.size > maxMb * 1024 * 1024) {
      setError(`File exceeds ${maxMb} MB limit`);
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const token = localStorage.getItem("auth_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", file.name);

      const endpoint = kind === "image" ? "/api/media/upload/image" : "/api/media/upload/pdf";

      // Use XHR for upload progress
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_URL}${endpoint}`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        });
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (err) {
              reject(new Error("Invalid response"));
            }
          } else {
            try {
              const data = JSON.parse(xhr.responseText);
              reject(new Error(data.detail || `Upload failed (${xhr.status})`));
            } catch {
              reject(new Error(`Upload failed (${xhr.status})`));
            }
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(formData);
      });

      onChange?.(result.media);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    onChange?.(null);
    setError(null);
  };

  const handleManualSubmit = () => {
    if (manualUrl.trim()) {
      onChange?.(manualUrl.trim());
      setManualUrl("");
      setShowManual(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const Icon = kind === "image" ? ImageIcon : FileText;

  return (
    <div data-testid={`${testIdPrefix}-field`}>
      {label && <label className="block text-white/70 text-sm mb-2">{label}</label>}

      {/* Existing value preview */}
      {displayUrl && !uploading && (
        <div className="mb-3 flex items-start gap-3 p-3 bg-white/[0.03] border border-white/10 rounded-lg">
          {kind === "image" ? (
            <img
              src={displayUrl}
              alt={displayTitle || "preview"}
              className="w-20 h-20 object-cover rounded border border-white/10"
              data-testid={`${testIdPrefix}-preview`}
            />
          ) : (
            <div className="w-20 h-20 flex items-center justify-center rounded border border-white/10 bg-blue-500/10">
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{displayTitle}</p>
            <p className="text-white/40 text-xs truncate">{displayUrl}</p>
            <div className="flex items-center gap-2 mt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-white/60 hover:text-white"
                onClick={() => window.open(displayUrl, "_blank")}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" />
                Open
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-white/60 hover:text-white"
                onClick={() => copy(displayUrl)}
              >
                <Copy className="w-3.5 h-3.5 mr-1" />
                URL
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-white/60 hover:text-red-400"
                onClick={handleRemove}
                data-testid={`${testIdPrefix}-remove`}
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`relative cursor-pointer border-2 border-dashed rounded-xl px-6 py-8 text-center transition-all
          ${dragOver ? "border-blue-400 bg-blue-500/10" : "border-white/10 bg-white/[0.02] hover:border-white/30 hover:bg-white/[0.04]"}
          ${uploading ? "pointer-events-none opacity-70" : ""}`}
        data-testid={`${testIdPrefix}-dropzone`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid={`${testIdPrefix}-input`}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-7 h-7 text-blue-400 animate-spin" />
            <p className="text-white/80 text-sm">Uploading… {progress}%</p>
            <div className="w-full max-w-xs h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
              <Upload className="w-5 h-5 text-blue-400" />
            </div>
            <p className="text-white text-sm font-medium">
              {displayUrl ? `Replace ${kind}` : `Drop ${kind} here or click to upload`}
            </p>
            <p className="text-white/40 text-xs">
              <Icon className="w-3 h-3 inline mr-1" />
              {kind === "image" ? "JPG · PNG · WebP · GIF" : "PDF"} · Max {maxMb} MB
            </p>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-red-400 text-xs" data-testid={`${testIdPrefix}-error`}>{error}</p>
      )}

      {helpText && !error && (
        <p className="mt-2 text-white/40 text-xs">{helpText}</p>
      )}

      {/* Manual URL fallback */}
      <div className="mt-2">
        {!showManual ? (
          <button
            type="button"
            onClick={() => setShowManual(true)}
            className="text-xs text-white/40 hover:text-white/70 underline underline-offset-2"
          >
            or paste an external URL
          </button>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            <Input
              value={manualUrl}
              onChange={(e) => setManualUrl(e.target.value)}
              placeholder={kind === "image" ? "https://…/image.jpg" : "https://…/file.pdf"}
              className="bg-white/5 border-white/10 text-white text-sm h-8"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 border-white/20 text-white"
              onClick={handleManualSubmit}
            >
              Use
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-white/50"
              onClick={() => { setShowManual(false); setManualUrl(""); }}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
